// src/components/AppPromoStrip.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window._API_BASE) ||
  "https://ecom-ads-ai-production.up.railway.app";

/* Defaults (no headline/subtext fallbacks) */
const DEFAULT_PROMO = {
  enabled: true,
  headline: "",
  subtext: "",
  links: [],
};

const PROMO_CSS_VAR = "--pixelads-promo-h";
const PROMO_STYLE_ID = "pixelads-app-promo-offset-style";

function asString(v, fallback = "") {
  if (v === null || v === undefined) return fallback;
  return String(v).trim();
}

// Avoid literal "javascript:" in CRA lint contexts
const JS_URL_PREFIX = String.fromCharCode(
  106, 97, 118, 97, 115, 99, 114, 105, 112, 116, 58
);
const DATA_URL_PREFIX = String.fromCharCode(100, 97, 116, 97, 58);

function isSafeHref(href) {
  const h = asString(href, "");
  if (!h) return false;

  const lower = h.toLowerCase();
  if (lower.startsWith(JS_URL_PREFIX) || lower.startsWith(DATA_URL_PREFIX))
    return false;

  if (lower.startsWith("http://") || lower.startsWith("https://")) return true;
  if (lower.startsWith("/")) return true;
  if (lower.startsWith("#")) return true;
  if (lower.startsWith("mailto:")) return true;

  return false;
}

function sanitizeLinks(links) {
  if (!Array.isArray(links)) return [];
  const out = [];

  for (const raw of links) {
    const label = asString(raw?.label, "").slice(0, 48);
    const href = asString(raw?.href, "").slice(0, 500);
    const newTab = !!raw?.newTab;

    if (!label || !isSafeHref(href)) continue;

    out.push({ label, href, newTab });
    if (out.length >= 5) break;
  }

  return out;
}

function sanitizePromo(input) {
  const enabled =
    typeof input?.enabled === "boolean" ? input.enabled : DEFAULT_PROMO.enabled;

  // ✅ no fallbacks: allow empty headline/subtext
  const headline = asString(input?.headline, "").slice(0, 120);
  const subtext = asString(input?.subtext, "").slice(0, 200);
  const links = sanitizeLinks(input?.links);

  return { enabled, headline, subtext, links };
}

function ensureGlobalOffsetStyle() {
  try {
    if (typeof document === "undefined") return;

    const existing = document.getElementById(PROMO_STYLE_ID);
    if (existing) return;

    const style = document.createElement("style");
    style.id = PROMO_STYLE_ID;

    style.textContent = `
      :root { ${PROMO_CSS_VAR}: 0px; }

      #root header.sticky.top-0,
      #root header.fixed.top-0 {
        top: var(${PROMO_CSS_VAR}, 0px) !important;
      }

      #root aside.fixed.top-0,
      #root nav.fixed.top-0,
      #root aside.sticky.top-0,
      #root nav.sticky.top-0 {
        top: var(${PROMO_CSS_VAR}, 0px) !important;
      }

      #root aside.fixed.top-0.h-screen,
      #root nav.fixed.top-0.h-screen {
        height: calc(100vh - var(${PROMO_CSS_VAR}, 0px)) !important;
      }
    `;

    document.head.appendChild(style);
  } catch {
    // ignore
  }
}

function setPromoCssHeight(px) {
  try {
    if (typeof document === "undefined") return;
    const h = Number.isFinite(px) ? Math.max(0, Math.round(px)) : 0;

    ensureGlobalOffsetStyle();
    document.documentElement.style.setProperty(PROMO_CSS_VAR, `${h}px`);
  } catch {
    // ignore
  }
}

/**
 * Fixed promo strip for authenticated pages.
 */
export default function AppPromoStrip({ onHeightChange }) {
  const { getToken } = useAuth();

  const [promo, setPromo] = useState(DEFAULT_PROMO);
  const ref = useRef(null);

  const measure = useCallback(() => {
    const h = ref.current?.getBoundingClientRect?.().height || 0;

    setPromoCssHeight(h);

    if (typeof onHeightChange === "function") {
      onHeightChange(h || 0);
    }
  }, [onHeightChange]);

  const loadPromo = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) throw new Error("no token");

      const r = await fetch(`${API_BASE}/api/app/promo-strip?ts=${Date.now()}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!r.ok) throw new Error(`promo http ${r.status}`);

      const data = await r.json();
      const next = sanitizePromo(data?.promo || {});
      setPromo(next);
    } catch (_e) {
      setPromo((prev) => prev || DEFAULT_PROMO);
    }
  }, [getToken]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!alive) return;
      await loadPromo();
      if (!alive) return;
      window.requestAnimationFrame(() => measure());
    };

    run();

    const onFocus = () => {
      loadPromo().then(() => window.requestAnimationFrame(() => measure()));
    };

    window.addEventListener("focus", onFocus);

    const interval = setInterval(() => {
      loadPromo().then(() => window.requestAnimationFrame(() => measure()));
    }, 60000);

    return () => {
      alive = false;
      window.removeEventListener("focus", onFocus);
      clearInterval(interval);

      setPromoCssHeight(0);
      if (typeof onHeightChange === "function") onHeightChange(0);
    };
  }, [loadPromo, measure, onHeightChange]);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => measure());

    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    let ro;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      ro = new ResizeObserver(() => measure());
      if (ref.current) ro.observe(ref.current);
    }

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [measure]);

  // If disabled, clear the offsets
  useEffect(() => {
    if (promo?.enabled) return;
    setPromoCssHeight(0);
    if (typeof onHeightChange === "function") onHeightChange(0);
  }, [promo?.enabled, onHeightChange]);

  if (!promo?.enabled) return null;

  const headline = asString(promo?.headline, "");
  const subtext = asString(promo?.subtext, "");
  const hasHeadline = Boolean(headline);
  const hasSubtext = Boolean(subtext);
  const hasText = hasHeadline || hasSubtext;

  return (
    <div
      ref={ref}
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#F3123C] via-[#FC2783] to-[#605DFF] text-white"
    >
      <div className="px-3 sm:px-8">
        <div className="mx-auto max-w-[1600px] py-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-2">
            {hasText ? (
              <div className="text-center sm:text-left text-xs sm:text-sm">
                {/* ✅ no fallbacks + no underline on headline/subtext */}
                {hasHeadline ? (
                  <span className="font-semibold">{headline}</span>
                ) : null}
                {hasSubtext ? (
                  <span className={hasHeadline ? "ml-1" : ""}>{subtext}</span>
                ) : null}
              </div>
            ) : null}

            {Array.isArray(promo?.links) && promo.links.length > 0 ? (
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
                {promo.links.map((l) => {
                  const key = `${l.label}-${l.href}`;
                  const target = l.newTab ? "_blank" : undefined;
                  const rel = l.newTab ? "noreferrer noopener" : undefined;

                  return (
                    <a
                      key={key}
                      href={l.href}
                      target={target}
                      rel={rel}
                      /* ✅ underline only for actual href links */
                      className="inline-flex items-center gap-1 text-xs sm:text-sm text-white/95 hover:text-white underline underline-offset-4 decoration-white/60 hover:decoration-white transition"
                    >
                      <span className="font-semibold">{l.label}</span>
                      {l.newTab ? (
                        <ExternalLink className="h-3.5 w-3.5 opacity-90" />
                      ) : null}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
